import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import Field from "./components/Field/Field";
import Cursor from "./components/Cursor/Cursor";
import Intro from "./components/Intro/Intro";
import PageTransition from "./components/PageTransition/PageTransition";
import FilmMode from "./components/FilmMode/FilmMode";
import BadgeDock from "./components/Lanyard/BadgeDock";
import { usePageIdle } from "./hooks/usePageIdle";
import OnePage from "./pages/OnePage";
import Resume from "./pages/Resume";
import { IntroMorphContext, type HeroTextMode, type IntroMorphState } from "./lib/introMorph";

const INTRO_KEY = "qh-intro-shown";

/**
 * Whether the boot intro should play at all: false for a reduced-motion
 * preference, and false again after the first time this browser tab has
 * already sat through it. Read once, synchronously, before first paint —
 * not in an effect — so a repeat visit within the same session never
 * flashes the intro for even one frame before deciding to skip it.
 */
function shouldPlayIntro(): boolean {
    try {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
        return sessionStorage.getItem(INTRO_KEY) !== "1";
    } catch {
        // Storage can throw in locked-down browser contexts (private mode in
        // some browsers, disabled storage). Fail toward *not* blocking the
        // site behind an intro that might then have nowhere to record
        // "already shown" and could in principle keep re-triggering.
        return false;
    }
}

/**
 * Separated from App so `useLocation` has a Router to read from — the hook
 * throws outside one, and Router is what App itself renders below.
 *
 * `mode="wait"` on AnimatePresence is deliberate: it holds the incoming page
 * off-screen until the outgoing one has fully finished its exit, rather than
 * cross-fading both at once. Two full pages animating simultaneously is
 * exactly the cluttered, unclear transition this is meant to replace.
 */
function AppRoutes() {
    const location = useLocation();

    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route
                    path="/"
                    element={
                        <PageTransition>
                            <OnePage />
                        </PageTransition>
                    }
                />
                <Route
                    path="/resume"
                    element={
                        <PageTransition>
                            <Resume />
                        </PageTransition>
                    }
                />
                <Route
                    path="*"
                    element={
                        <PageTransition>
                            <OnePage />
                        </PageTransition>
                    }
                />
            </Routes>
        </AnimatePresence>
    );
}

function App() {
    usePageIdle();
    const [playIntro] = useState(shouldPlayIntro);
    const [showIntro, setShowIntro] = useState(playIntro);
    /*
     * Starts at "wait" only when an intro is actually going to play; a repeat
     * visit this session (or reduced-motion) goes straight to "normal" so
     * Home renders its ordinary independent reveal immediately, exactly as it
     * did before Intro existed — nothing here is holding it back.
     */
    const [heroTextMode, setHeroTextMode] = useState<HeroTextMode>(
        playIntro ? "wait" : "normal"
    );

    // Recorded at mount, not at completion — the point is "the intro has
    // already started for this tab," so a reload mid-animation still counts
    // as shown rather than replaying from the top.
    useEffect(() => {
        if (!playIntro) return;
        try {
            sessionStorage.setItem(INTRO_KEY, "1");
        } catch {
            // Nothing to do if storage is unavailable — the intro will just
            // play again next time, which is the safe direction to fail in.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const introMorphState: IntroMorphState = { heroTextMode, introActive: showIntro };

    return (
        <IntroMorphContext.Provider value={introMorphState}>
            <Router>
                <Field />
                {/* Mounts itself out of existence on touch and under reduced
                    motion — see Cursor.tsx. */}
                <Cursor />
                <Navbar />
                {/* The lanyard card, docked in front of the navbar once the
                    reader leaves Index (desktop story mode only). */}
                <BadgeDock />
                <AppRoutes />
                <Footer />
                {/* "Play" — auto-scrolls the page like a film. */}
                <FilmMode />
                {showIntro && (
                    <Intro
                        onMorphStart={() => setHeroTextMode("morph")}
                        onDone={() => setShowIntro(false)}
                    />
                )}
            </Router>
        </IntroMorphContext.Provider>
    );
}

export default App;
